import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, updateDoc, query, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import './ProductApplications.css';

function ProductApplications() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProductAndApplications();
  }, [productId]);

  const fetchProductAndApplications = async () => {
    try {
      const productDoc = await getDoc(doc(db, 'products', productId));
      if (productDoc.exists()) {
        setProduct({ id: productDoc.id, ...productDoc.data() });
      }

      const q = query(collection(db, 'applications'), where('productId', '==', productId));
      const querySnapshot = await getDocs(q);
      const apps = [];
      querySnapshot.forEach((doc) => {
        apps.push({ id: doc.id, ...doc.data() });
      });
      
      apps.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toMillis() - a.createdAt.toMillis();
        }
        return 0;
      });
      
      setApplications(apps);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus.toUpperCase()} this application? This decision is FINAL and cannot be changed.`)) {
      return;
    }

    try {
      const appRef = doc(db, 'applications', applicationId);
      await updateDoc(appRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      setApplications(applications.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating application status');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    return app.status === filter;
  });

  if (loading) {
    return (
      <div className="product-apps">
        <div className="loading">Loading applications...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-apps">
        <div className="loading">Product not found</div>
      </div>
    );
  }

  return (
    <div className="product-apps">
      <nav className="apps-nav">
        <div className="nav-container">
          <div className="logo">BetaBuyers Admin</div>
          <div className="nav-buttons">
            <button onClick={() => navigate('/admin-dashboard')} className="btn-nav">Dashboard</button>
            <button onClick={() => navigate('/products')} className="btn-nav">Products</button>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </div>
      </nav>

      <div className="apps-content">
        <div className="apps-header">
          <div>
            <button onClick={() => navigate('/products')} className="btn-back">← Back to Products</button>
            <h1>{product.name}</h1>
            <p className="product-desc">{product.description}</p>
          </div>
        </div>

        <div className="filter-section">
          <div className="filter-buttons">
            <button 
              className={`btn-filter ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({applications.length})
            </button>
            <button 
              className={`btn-filter ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending ({applications.filter(a => a.status === 'pending').length})
            </button>
            <button 
              className={`btn-filter ${filter === 'approved' ? 'active' : ''}`}
              onClick={() => setFilter('approved')}
            >
              Approved ({applications.filter(a => a.status === 'approved').length})
            </button>
            <button 
              className={`btn-filter ${filter === 'rejected' ? 'active' : ''}`}
              onClick={() => setFilter('rejected')}
            >
              Rejected ({applications.filter(a => a.status === 'rejected').length})
            </button>
          </div>
        </div>

        {filteredApplications.length === 0 ? (
          <div className="no-applications">
            <p>No {filter !== 'all' ? filter : ''} applications yet</p>
          </div>
        ) : (
          <div className="applications-grid">
            {filteredApplications.map(app => (
              <div key={app.id} className={`application-card ${app.status}`}>
                
                {/* Card Header */}
                <div className="card-header">
                  <span className="submission-date">
                    {app.createdAt ? new Date(app.createdAt.toMillis()).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    }) : 'N/A'}
                  </span>
                  <div className={`status-badge-large ${app.status}`}>
                    {app.status === 'approved' && (
                      <>
                        <span className="status-icon">✓</span>
                        <span>APPROVED</span>
                      </>
                    )}
                    {app.status === 'rejected' && (
                      <>
                        <span className="status-icon">✗</span>
                        <span>REJECTED</span>
                      </>
                    )}
                    {app.status === 'pending' && (
                      <>
                        <span className="status-icon">⏳</span>
                        <span>PENDING REVIEW</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Status Banner for Approved/Rejected */}
                {app.status === 'approved' && (
                  <div className="status-banner approved">
                    <span className="banner-icon">✓</span>
                    <div className="banner-content">
                      <strong>APPLICATION APPROVED</strong>
                      <p>This applicant has been approved for beta testing</p>
                    </div>
                  </div>
                )}

                {app.status === 'rejected' && (
                  <div className="status-banner rejected">
                    <span className="banner-icon">✗</span>
                    <div className="banner-content">
                      <strong>APPLICATION REJECTED</strong>
                      <p>This applicant was not selected for beta testing</p>
                    </div>
                  </div>
                )}

                {/* Application Content */}
                <div className="app-content">
                  <div className="app-field">
                    <label>Email</label>
                    <p>{app.email}</p>
                  </div>

                  <div className="app-field">
                    <label>Why test this product?</label>
                    <p>{app.reason}</p>
                  </div>

                  <div className="app-field">
                    <label>Experience</label>
                    <p>{app.experience}</p>
                  </div>

                  {app.contact && (
                    <div className="app-field">
                      <label>Contact</label>
                      <p>{app.contact}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons - ONLY for pending applications */}
                {app.status === 'pending' && (
                  <div className="app-actions">
                    <button 
                      onClick={() => handleStatusUpdate(app.id, 'approved')}
                      className="btn-approve"
                    >
                      ✓ Approve Application
                    </button>
                    <button 
                      onClick={() => handleStatusUpdate(app.id, 'rejected')}
                      className="btn-reject"
                    >
                      ✗ Reject Application
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductApplications;